"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.combineGroupedRecords = exports.combineGroupedMaps = exports.removeProperty = exports.filterUndefinedProps = exports.trimStart = exports.trimEnd = exports.concat = exports.map = exports.forEach = exports.toRecord = exports.toMap = exports.toGroupedRecord = exports.toGroupedMap = exports.isNumber = exports.isFunction = exports.isObject = exports.toNonNullableArray = exports.toArray = exports.isNotUndefined = exports.isNotNil = exports.isNil = void 0;
/* eslint-disable no-restricted-syntax */
/**
 * Checks if input is null or undefined
 */
function isNil(input) {
    return input === null || input === undefined;
}
exports.isNil = isNil;
/**
 * Checks if input is not null or undefined
 */
function isNotNil(input) {
    return !isNil(input);
}
exports.isNotNil = isNotNil;
/**
 * Not undefined type guard to use in filters
 */
function isNotUndefined(input) {
    return input !== undefined;
}
exports.isNotUndefined = isNotUndefined;
function toArray(input) {
    return Array.isArray(input) ? input : [input];
}
exports.toArray = toArray;
/**
 * Converts an input to an array (if it is not already one) and filters out null/undefined values
 */
function toNonNullableArray(input) {
    return toArray(input).filter(isNotNil);
}
exports.toNonNullableArray = toNonNullableArray;
/**
 * Checks if val is an object.
 */
function isObject(val) {
    return !isNil(val) && typeof val === 'object';
}
exports.isObject = isObject;
/**
 * Checks if val is a function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isFunction(val) {
    return typeof val === 'function';
}
exports.isFunction = isFunction;
/**
 * Checks if val is a number. Also checks for NaN
 */
function isNumber(val) {
    return typeof val === 'number' && !Number.isNaN(val);
}
exports.isNumber = isNumber;
function toGroupedMap(items, keySelector, valueSelector) {
    const localValSelector = valueSelector === undefined ? (item) => item : valueSelector;
    const localMap = new Map();
    forEach(items, (item, index) => {
        const key = keySelector(item, index);
        const value = localValSelector(item, index);
        const group = localMap.get(key);
        if (group !== undefined) {
            group.push(value);
        }
        else {
            localMap.set(key, [value]);
        }
    });
    return localMap;
}
exports.toGroupedMap = toGroupedMap;
function toGroupedRecord(items, keySelector, valueSelector) {
    const localValSelector = valueSelector === undefined ? (item) => item : valueSelector;
    const localRecord = {};
    forEach(items, (item, index) => {
        const key = keySelector(item, index);
        const value = localValSelector(item, index);
        const group = localRecord[key];
        if (group !== undefined) {
            group.push(value);
        }
        else {
            localRecord[key] = [value];
        }
    });
    return localRecord;
}
exports.toGroupedRecord = toGroupedRecord;
function toMap(items, keySelector, valueSelector) {
    const localValSelector = valueSelector === undefined ? (item) => item : valueSelector;
    const newMap = new Map();
    forEach(items, (item, index) => newMap.set(keySelector(item, index), localValSelector(item, index)));
    return newMap;
}
exports.toMap = toMap;
function toRecord(items, keySelector, valueSelector) {
    const localValSelector = valueSelector === undefined ? (item) => item : valueSelector;
    const newRecord = {};
    forEach(items, (item, index) => {
        newRecord[keySelector(item, index)] = localValSelector(item, index);
    });
    return newRecord;
}
exports.toRecord = toRecord;
/**
 * Invokes the callback function on every item in the iterable
 */
function forEach(items, callback) {
    let index = 0;
    for (const item of items) {
        callback(item, index);
        index += 1;
    }
}
exports.forEach = forEach;
/**
 * Invokes the callback function on every item and returns a new iterable with the mapper item
 */
function* map(items, callback) {
    let index = 0;
    for (const item of items) {
        yield callback(item, index);
        index += 1;
    }
}
exports.map = map;
/**
 * Takes an iterable of iterables and returns a single new iterable that concats each one
 */
function* concat(iterables) {
    for (const iterable of iterables) {
        yield* iterable;
    }
}
exports.concat = concat;
/**
 * Trims off the provided substring from the start of the string if it exists
 */
function trimEnd(input, substr) {
    return input.endsWith(substr) ? input.substring(0, input.length - substr.length) : input;
}
exports.trimEnd = trimEnd;
/**
 * * Trims off the provided substring from the end of the string if it exists
 */
function trimStart(input, substr) {
    return input.startsWith(substr) ? input.substring(substr.length) : input;
}
exports.trimStart = trimStart;
/**
 * Removes properties that are present but set to undefined.
 * This method does not mutate the input.
 */
function filterUndefinedProps(entity) {
    return Object.keys(entity).reduce((updatedEntity, propName) => removeUndefinedProperty(updatedEntity, propName), { ...entity });
}
exports.filterUndefinedProps = filterUndefinedProps;
function removeUndefinedProperty(entity, propName) {
    const updatedEntity = { ...entity };
    if (updatedEntity[propName] === undefined) {
        delete updatedEntity[propName];
        return updatedEntity;
    }
    return updatedEntity;
}
/**
 * Removes the specified property from the object.
 * This method does not mutate the input and instead returns a new object with the property omitted.
 */
function removeProperty(entity, propName) {
    const updatedEntity = { ...entity };
    delete updatedEntity[propName];
    return updatedEntity;
}
exports.removeProperty = removeProperty;
function combineGroupedMaps(maps, valueSelector) {
    const localValueSelector = valueSelector !== null && valueSelector !== void 0 ? valueSelector : ((value) => value);
    return toGroupedMap(concat(map(maps, (record) => getGroupedMapEntityIterable(record))), ([key]) => key, ([, value]) => localValueSelector(value));
}
exports.combineGroupedMaps = combineGroupedMaps;
function* getGroupedMapEntityIterable(maps) {
    for (const [key, values] of maps.entries()) {
        for (const value of values) {
            yield [key, value];
        }
    }
}
function combineGroupedRecords(records, valueSelector) {
    const localValueSelector = valueSelector !== null && valueSelector !== void 0 ? valueSelector : ((value) => value);
    return toGroupedRecord(concat(map(records, (record) => getGroupedRecordEntityIterable(record))), ([key]) => key, ([, value]) => localValueSelector(value));
}
exports.combineGroupedRecords = combineGroupedRecords;
function* getGroupedRecordEntityIterable(record) {
    for (const [key, values] of Object.entries(record)) {
        for (const value of values) {
            yield [key, value];
        }
    }
}
//# sourceMappingURL=generic.js.map